import { formatMoney } from '@theme/money-formatting';

const make = (tag, className, value) => {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (value != null) element.textContent = String(value);
  return element;
};

class VivaHeaderSearch extends HTMLElement {
  #events = new AbortController();
  #fetchController = null;
  #timer = null;
  #requestVersion = 0;
  #lastQuery = '';
  #suppressNextFocus = false;

  connectedCallback() {
    this.#events = new AbortController();
    this.input = this.querySelector('.vhs__input');
    this.form = this.querySelector('.vhs__form');
    this.panel = this.querySelector('.vhs__panel');
    this.results = this.querySelector('[data-vhs-results]');
    this.discovery = this.querySelector('[data-vhs-discovery]');
    this.recent = this.querySelector('[data-vhs-recent]');
    this.all = this.querySelector('[data-vhs-all]');
    this.status = this.querySelector('[data-vhs-status]');
    if (!this.input || !this.form || !this.panel || !this.results || !this.discovery || !this.recent || !this.all) return;

    const { signal } = this.#events;
    this.input.addEventListener('focus', this.#onFocus, { signal });
    this.input.addEventListener('input', this.#onInput, { signal });
    this.form.addEventListener('submit', this.#onSubmit, { signal });
    this.addEventListener('keydown', this.#onKeyDown, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    this.panel.addEventListener('click', this.#onPanelClick, { signal });
    document.addEventListener('pointerdown', this.#onOutsidePointer, { signal });
  }

  disconnectedCallback() {
    this.#events.abort();
    this.#cancelPending();
  }

  closeSearch() {
    this.#close();
  }

  #onFocus = () => {
    if (this.#suppressNextFocus) {
      this.#suppressNextFocus = false;
      return;
    }
    const query = this.input.value.trim();
    if (query.length < 2) {
      this.#showDiscovery();
    } else if (query === this.#lastQuery && this.results.childElementCount) {
      this.#open();
    } else {
      this.#queueSearch(query);
    }
  };

  #onInput = () => {
    const query = this.input.value.trim();
    if (query.length < 2) {
      this.#showDiscovery();
    } else {
      this.#queueSearch(query);
    }
  };

  #onSubmit = (event) => {
    const query = this.input.value.trim();
    if (!query) {
      event.preventDefault();
      this.input.focus();
      this.#showDiscovery();
      return;
    }
    this.#saveRecent(query);
  };

  #onKeyDown = (event) => {
    if (event.key === 'Escape' && !this.panel.hidden) {
      event.preventDefault();
      if (document.activeElement !== this.input) this.#suppressNextFocus = true;
      this.#close();
      this.input.focus();
      return;
    }
    if (this.panel.hidden || (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')) return;
    const links = [...this.panel.querySelectorAll('a[href]')].filter((link) => !link.closest('[hidden]'));
    if (!links.length) return;
    event.preventDefault();
    const current = links.indexOf(document.activeElement);
    const next = event.key === 'ArrowDown'
      ? (current + 1) % links.length
      : (current < 0 ? links.length - 1 : (current - 1 + links.length) % links.length);
    links[next].focus();
    links[next].scrollIntoView({ block: 'nearest' });
  };

  #onFocusOut = () => {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.#close();
    }, 0);
  };

  #onPanelClick = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('a[href]')) return;
    this.#saveRecent(this.input.value.trim());
    this.#close();
  };

  #onOutsidePointer = (event) => {
    if (event.target instanceof Node && !this.contains(event.target)) this.#close();
  };

  #open() {
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  #close() {
    this.panel.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.#cancelPending();
  }

  #cancelPending() {
    if (this.#timer != null) clearTimeout(this.#timer);
    this.#timer = null;
    this.#fetchController?.abort();
    this.#fetchController = null;
    this.#requestVersion += 1;
  }

  #searchHref(query) {
    const url = new URL(this.dataset.searchUrl || '/search', location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    return `${url.pathname}${url.search}`;
  }

  #safeHref(value) {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
      const url = new URL(value, location.origin);
      if (url.origin !== location.origin) return null;
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return null;
    }
  }

  #safeImage(value) {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
      const url = new URL(value, location.origin);
      if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
      if (url.hostname === 'cdn.shopify.com') url.searchParams.set('width', '116');
      return url.href;
    } catch {
      return null;
    }
  }

  #currency() {
    return this.dataset.currency || window.Shopify?.currency?.active || 'EUR';
  }

  #money(value) {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '';
    const currency = this.#currency();
    try {
      const precision = new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits;
      const minor = Math.round(amount * (10 ** precision));
      const output = formatMoney(minor, this.dataset.moneyFormat || '{{amount_with_comma_separator}}', currency);
      return new DOMParser().parseFromString(output, 'text/html').body.textContent?.trim() || '';
    } catch {
      try {
        return new Intl.NumberFormat(this.dataset.locale || 'en', { style: 'currency', currency }).format(amount);
      } catch {
        return String(amount);
      }
    }
  }

  #readRecent() {
    try {
      const value = JSON.parse(localStorage.getItem(`viva-header-search:${this.dataset.locale || 'en'}`) || '[]');
      return Array.isArray(value) ? value.filter((item) => typeof item === 'string').slice(0, 4) : [];
    } catch {
      return [];
    }
  }

  #saveRecent(value) {
    const query = String(value || '').trim().slice(0, 80);
    if (query.length < 2) return;
    try {
      const recent = this.#readRecent().filter((item) => item.toLocaleLowerCase() !== query.toLocaleLowerCase());
      localStorage.setItem(`viva-header-search:${this.dataset.locale || 'en'}`, JSON.stringify([query, ...recent].slice(0, 4)));
    } catch {
      // Private browsing can disable local storage; search still works.
    }
  }

  #renderRecent() {
    const recent = this.#readRecent();
    this.recent.replaceChildren();
    this.recent.hidden = recent.length === 0;
    if (!recent.length) return;
    this.recent.append(make('p', 'vhs__heading', this.dataset.recentLabel));
    const list = make('ul', 'vhs__links');
    for (const query of recent) {
      const item = make('li');
      const link = make('a', 'vhs__item vhs__item--collection', query);
      link.href = this.#searchHref(query);
      item.append(link);
      list.append(item);
    }
    this.recent.append(list);
  }

  #showDiscovery() {
    this.#cancelPending();
    this.#renderRecent();
    this.results.hidden = true;
    this.discovery.hidden = false;
    this.all.hidden = true;
    if (this.status) this.status.textContent = '';
    this.#open();
  }

  #showMessage(message, query) {
    this.results.replaceChildren(make('p', 'vhs__message', message));
    this.results.hidden = false;
    this.recent.hidden = true;
    this.discovery.hidden = false;
    this.all.href = this.#searchHref(query);
    this.all.hidden = false;
    if (this.status) this.status.textContent = message;
    this.#open();
  }

  #queueSearch(query) {
    this.#cancelPending();
    this.#showMessage(this.dataset.loading || 'Searching…', query);
    const version = this.#requestVersion;
    this.#timer = setTimeout(() => this.#search(query, version), 220);
  }

  async #search(query, version) {
    this.#timer = null;
    if (version !== this.#requestVersion || query !== this.input.value.trim()) return;
    const url = new URL(this.dataset.suggestUrl || '/search/suggest.json', location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product,collection');
    url.searchParams.set('resources[limit]', '5');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    const controller = new AbortController();
    this.#fetchController = controller;
    try {
      const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Search returned ${response.status}`);
      const payload = await response.json();
      if (version !== this.#requestVersion || query !== this.input.value.trim()) return;
      this.#lastQuery = query;
      this.#renderResults(payload.resources?.results, query);
    } catch (error) {
      if (controller.signal.aborted || version !== this.#requestVersion) return;
      this.#showMessage(this.dataset.error || 'Suggestions are unavailable. Press Enter for all results.', query);
    } finally {
      if (this.#fetchController === controller) this.#fetchController = null;
    }
  }

  #renderResults(data, query) {
    const products = Array.isArray(data?.products) ? data.products.slice(0, 5) : [];
    const collections = Array.isArray(data?.collections) ? data.collections.slice(0, 3) : [];
    const fragment = document.createDocumentFragment();
    const productGroup = this.#makeGroup(this.dataset.productsLabel || 'Products', products, (item) => this.#makeProduct(item));
    const collectionGroup = this.#makeGroup(this.dataset.collectionsLabel || 'Collections', collections, (item) => this.#makeCollection(item));
    if (productGroup) fragment.append(productGroup);
    if (collectionGroup) fragment.append(collectionGroup);

    if (!fragment.childNodes.length) {
      this.#showMessage(this.dataset.noResults || 'No exact matches.', query);
      return;
    }
    this.results.replaceChildren(fragment);
    this.results.hidden = false;
    this.recent.hidden = true;
    this.discovery.hidden = true;
    this.all.href = this.#searchHref(query);
    this.all.hidden = false;
    if (this.status) this.status.textContent = this.dataset.resultsUpdated || 'Suggestions updated';
    this.#open();
  }

  #makeGroup(title, items, build) {
    const list = make('ul', 'vhs__links');
    for (const item of items) {
      const link = build(item);
      if (!link) continue;
      const listItem = make('li');
      listItem.append(link);
      list.append(listItem);
    }
    if (!list.childElementCount) return null;
    const group = make('div', 'vhs__group');
    group.append(make('p', 'vhs__heading', title), list);
    return group;
  }

  #makeCollection(collection) {
    const href = this.#safeHref(collection?.url);
    if (!href) return null;
    const link = make('a', 'vhs__item vhs__item--collection');
    link.href = href;
    link.append(make('span', '', collection.title || ''));
    return link;
  }

  #makeProduct(product) {
    const href = this.#safeHref(product?.url);
    if (!href) return null;
    const link = make('a', 'vhs__item vhs__item--product');
    link.href = href;
    const imageBox = make('span', 'vhs__image');
    const imageSource = this.#safeImage(product.featured_image?.url || product.image);
    if (imageSource) {
      const image = make('img');
      image.src = imageSource;
      image.alt = '';
      image.width = 58;
      image.height = 58;
      image.loading = 'lazy';
      image.decoding = 'async';
      imageBox.append(image);
    }
    const copy = make('span', 'vhs__copy');
    copy.append(make('span', 'vhs__title', product.title || ''));
    const meta = [product.vendor, product.type].filter(Boolean).join(' · ');
    if (meta) copy.append(make('span', 'vhs__meta', meta));
    copy.append(make('span', `vhs__stock${product.available === false ? ' vhs__stock--out' : ''}`,
      product.available === false ? this.dataset.soldOut : this.dataset.inStock));
    const price = make('span', 'vhs__price');
    const currentPrice = Number(product.price);
    let compareAt = null;
    const matchingVariant = Array.isArray(product.variants)
      ? product.variants.find((variant) => Number(variant.price) === currentPrice && Number(variant.compare_at_price) > currentPrice)
      : null;
    if (matchingVariant) compareAt = Number(matchingVariant.compare_at_price);
    else if (Number(product.price_min) === Number(product.price_max)) compareAt = Number(product.compare_at_price_min);
    if (compareAt > currentPrice) price.append(make('s', '', this.#money(compareAt)));
    if (Number(product.price_max) > Number(product.price_min) && !matchingVariant) {
      price.append(make('small', '', this.dataset.from || 'From'));
    }
    price.append(make('strong', '', this.#money(currentPrice)));
    link.append(imageBox, copy, price);
    return link;
  }
}

if (!customElements.get('viva-header-search')) customElements.define('viva-header-search', VivaHeaderSearch);

class VivaMobileSearchToggle extends HTMLElement {
  #events = new AbortController();

  connectedCallback() {
    this.#events = new AbortController();
    this.button = this.querySelector('button');
    this.searchSlot = document.getElementById(this.button?.getAttribute('aria-controls'));
    if (!this.button || !this.searchSlot) return;

    const { signal } = this.#events;
    this.button.addEventListener('click', this.#onClick, { signal });
    document.addEventListener('keydown', this.#onKeyDown, { signal });
    document.addEventListener('pointerdown', this.#onPointerDown, { signal });
    this.breakpoint = window.matchMedia('(min-width: 1024px)');
    this.breakpoint.addEventListener('change', this.#onBreakpoint, { signal });
  }

  disconnectedCallback() {
    this.#events.abort();
  }

  #onClick = () => {
    if (this.searchSlot.hidden) {
      this.searchSlot.hidden = false;
      this.button.setAttribute('aria-expanded', 'true');
      this.searchSlot.querySelector('.vhs__input')?.focus();
    } else {
      this.#close(true);
    }
  };

  #onKeyDown = (event) => {
    if (event.key !== 'Escape' || this.searchSlot.hidden) return;
    event.preventDefault();
    this.#close(true);
  };

  #onPointerDown = (event) => {
    if (this.searchSlot.hidden || !(event.target instanceof Node)) return;
    if (!this.contains(event.target) && !this.searchSlot.contains(event.target)) this.#close();
  };

  #onBreakpoint = () => {
    if (this.breakpoint.matches) this.#close();
  };

  #close(restoreFocus = false) {
    if (this.searchSlot.hidden) return;
    this.searchSlot.querySelector('viva-header-search')?.closeSearch();
    this.searchSlot.querySelector('.vhs__input')?.blur();
    this.searchSlot.hidden = true;
    this.button.setAttribute('aria-expanded', 'false');
    if (restoreFocus) this.button.focus();
  }
}

if (!customElements.get('viva-mobile-search-toggle')) customElements.define('viva-mobile-search-toggle', VivaMobileSearchToggle);
